import { axiosInstance } from "./axiosInstance";
import { logger } from "../utils/logger";
import { parseApiError } from "../types/errors";
import { handleError } from "../utils/errorHandler";
import {
  startRegistration,
  startAuthentication,
} from "@simplewebauthn/browser";

export interface LoginCredentials {
  email: string;
  password: string;
  recaptchaToken?: string;
}

export interface SignupCredentials {
  email: string;
  password: string;
  role?: string;
  firstName?: string;
  lastName?: string;
  recaptchaToken?: string;
  selectedPlan?: string;
  referralCode?: string;
}

export interface User {
  uid: string;
  email: string;
  role: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  profileImage?: string;
  emailVerified: boolean;
  authProvider?: string;
  lastLoginAt?: string;
  loginCount?: number;
  defaultTemplateId?: string; // ID de la plantilla predeterminada para SOAPvett
  signature?: string; // Firma digital del veterinario
  groupId?: string; // Group membership for free specialist consultations
}

// Tipos para el perfil extendido del especialista
export interface EducationEntry {
  degree: string;
  institution: string;
  year: string;
  description?: string;
}

export interface WorkPlace {
  name: string;
  position: string;
  location: string;
  startDate: string;
  endDate?: string;
}

export interface ResearchStudy {
  title: string;
  description: string;
  year: string;
  status?: string;
}

export interface HighlightedCase {
  title: string;
  description: string;
  year: string;
  outcome?: string;
}

export interface Certification {
  name: string;
  issuer: string;
  year: string;
  expiryDate?: string;
}

export interface ExtendedProfile {
  biography?: string;
  profileImage?: string;
  education?: EducationEntry[];
  workPlace?: WorkPlace;
  researchStudies?: ResearchStudy[];
  highlightedCases?: HighlightedCase[];
  yearsOfExperience?: number;
  languages?: string[];
  certifications?: Certification[];
  extendedProfileUpdatedAt?: string;
}

export interface SpecialistExtendedProfile extends User {
  specialty?: string;
  extendedProfile?: ExtendedProfile;
}

// Función helper para verificar si estamos en una ruta pública
const isPublicRoute = (): boolean => {
  const currentPath = window.location.pathname;
  const publicRoutes = [
    "/",
    "/home",
    "/login",
    "/signup",
    "/services",
    "/pricing",
    "/about",
    "/contact",
    "/verify-email",
    "/reset-password",
    "/forgot-password",
  ];

  // Verificar rutas exactas
  if (publicRoutes.includes(currentPath)) {
    return true;
  }

  // Verificar rutas que empiecen con paths públicos (ej: /reset-password/token)
  const publicPrefixes = ["/reset-password", "/verify-email"];
  return publicPrefixes.some((prefix) => currentPath.startsWith(prefix));
};

// Función helper para verificar si el usuario es el mega administrador
const isMegaAdmin = (user: User | null): boolean => {
  if (!user) return false;
  return user.email === "vettconsult.web@gmail.com" && user.role === "admin";
};

// Ya no creamos una instancia local, usamos la compartida desde axiosInstance

const authService = {
  async login(
    credentials: LoginCredentials,
  ): Promise<{ token: string; user: User }> {
    try {
      const response = await axiosInstance.post(`/auth/login`, credentials);
      if (response.data.token) {
        localStorage.setItem("token", response.data.token);

        const userInfo = await this.getCurrentUser();

        return { token: response.data.token, user: userInfo };
      }

      throw new Error("No se recibió un token de autenticación");
    } catch (error) {
      console.error("Error al iniciar sesión:", error);
      throw error;
    }
  },

  async signup(credentials: SignupCredentials): Promise<{ uid: string; firebaseCustomToken: string | null }> {
    try {
      const data = { ...credentials, role: "user" };
      const response = await axiosInstance.post(`/auth/register`, data);
      return response.data;
    } catch (error) {
      console.error("Error al registrar usuario:", error);
      throw error;
    }
  },

  async registerSpecialist(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    specialty: string;
  }): Promise<{ uid: string }> {
    try {
      const payload = { ...data, role: "teacher" };
      const response = await axiosInstance.post(`/auth/register`, payload);
      return response.data;
    } catch (error) {
      console.error("Error al registrar especialista:", error);
      throw error;
    }
  },

  async registerUser(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role: string;
    specialty?: string;
  }): Promise<{ uid: string }> {
    try {
      // Mapear roles para compatibilidad con el backend
      let backendRole = data.role;
      if (data.role === "especialista") {
        backendRole = "teacher";
      } else if (data.role === "cliente") {
        backendRole = "user";
      }

      const payload = {
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        role: backendRole,
        ...(data.specialty && { specialty: data.specialty }),
      };

      const response = await axiosInstance.post(`/auth/register`, payload);
      return response.data;
    } catch (error) {
      console.error("Error al registrar usuario:", error);
      throw error;
    }
  },

  logout(): void {
    const token = localStorage.getItem("token");
    if (token) {
      axiosInstance
        .post(`/auth/logout`, {})
        .catch((err) => console.error("Error al cerrar sesión:", err));
    }

    const userStr = localStorage.getItem("user");
    let isAdmin = false;

    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        isAdmin = user.role === "admin";
      } catch (e) {
        console.error("Error al parsear usuario:", e);
      }
    }

    // Siempre eliminar token y datos de usuario
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    // Redirección solo si NO es administrador o si es una salida explícita
    // Usar setTimeout para asegurar que primero se limpien los datos de sesión
    if (!isAdmin || window.location.pathname === "/admin") {
      setTimeout(() => {
        // Solo redirigir si no estamos ya en la página de login
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }, 100);
    }
  },

  async getCurrentUser(): Promise<User> {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    // Si estamos en una ruta pública y no hay token, devolver usuario vacío
    if (!token && isPublicRoute()) {
      if (storedUser) {
        try {
          return JSON.parse(storedUser) as User;
        } catch (e) {
          console.warn("[AUTH_SERVICE] Error parsing stored user, clearing it");
          localStorage.removeItem("user");
        }
      }

      return {
        uid: "",
        email: "",
        role: "none",
        emailVerified: false,
      };
    }

    // Si no hay token pero no estamos en ruta pública, es un error
    if (!token) {
      throw new Error("No hay token de autenticación");
    }

    try {
      // Hacer la petición al backend con el token en el header
      const response = await axiosInstance.get(`/auth/me`);

      // Verificar explícitamente el estado de emailVerified
      const userData = response.data;

      // Asegurar que emailVerified sea un booleano
      if (
        userData.emailVerified === undefined ||
        userData.emailVerified === null
      ) {
        console.warn(
          "[AUTH_SERVICE] emailVerified es undefined/null, estableciendo como false",
        );
        userData.emailVerified = false;
      } else {
        // Convertir explícitamente a booleano
        userData.emailVerified = userData.emailVerified === true;
      }

      // Guardar usuario en localStorage para acceso rápido
      localStorage.setItem("user", JSON.stringify(userData));

      return userData;
    } catch (error: unknown) {
      const handledError = handleError(error);
      console.error(
        "[AUTH_SERVICE] Error al obtener usuario actual:",
        handledError,
      );

      // Si falla pero estamos en ruta pública, devolver usuario vacío en lugar de error
      if (isPublicRoute()) {
        return {
          uid: "",
          email: "",
          role: "none",
          emailVerified: false,
        };
      }

      throw handledError;
    }
  },

  // Verificar si el usuario está autenticado
  isAuthenticated(): boolean {
    return !!localStorage.getItem("token");
  },

  // Verificar si el usuario actual es el mega administrador
  isMegaAdmin(): boolean {
    try {
      const userStr = localStorage.getItem("user");
      if (!userStr) return false;

      const user = JSON.parse(userStr) as User;
      return isMegaAdmin(user);
    } catch (error) {
      console.warn("[AUTH_SERVICE] Error checking mega admin status:", error);
      return false;
    }
  },

  // Obtener el token actual
  getToken(): string | null {
    return localStorage.getItem("token");
  },

  // Obtener Firebase custom token para sign-in directo con Firebase Auth
  async getFirebaseToken(): Promise<string> {
    const response = await axiosInstance.get(`/auth/firebase-token`);
    return response.data.firebaseToken;
  },

  // Iniciar sesión con Google
  async loginWithGoogle(
    googleToken: string,
  ): Promise<{ token: string; user: User }> {
    try {
      const response = await axiosInstance.post(`/auth/google`, {
        idToken: googleToken, // ✅ Corregido: backend espera "idToken"
      });

      if (response.data.token) {
        localStorage.setItem("token", response.data.token);

        // Usar los datos del usuario del backend si están disponibles
        if (response.data.user) {
          return { token: response.data.token, user: response.data.user };
        }

        const userInfo = await this.getCurrentUser();
        return { token: response.data.token, user: userInfo };
      }

      throw new Error("No se recibió un token de autenticación");
    } catch (error) {
      console.error("Error al iniciar sesión con Google:", error);
      throw error;
    }
  },

  async signupWithGoogle(
    googleToken: string,
  ): Promise<{ token: string; user: User }> {
    try {
      // ✅ Usar el mismo endpoint /auth/google - maneja tanto login como signup
      const response = await axiosInstance.post(`/auth/google`, {
        idToken: googleToken, // ✅ Corregido: backend espera "idToken"
      });

      if (response.data.token) {
        localStorage.setItem("token", response.data.token);

        // Si el backend devuelve los datos del usuario, los usamos directamente
        if (response.data.user) {
          return { token: response.data.token, user: response.data.user };
        }

        // Si no, obtenemos los datos del usuario por separado
        const userInfo = await this.getCurrentUser();
        return { token: response.data.token, user: userInfo };
      }

      throw new Error("No se recibió un token de autenticación");
    } catch (error) {
      console.error("Error al registrarse con Google:", error);
      throw error;
    }
  },

  // Iniciar sesión con Apple
  async loginWithApple(
    appleToken: string,
  ): Promise<{ token: string; user: User }> {
    try {
      const response = await axiosInstance.post(`/auth/apple`, {
        identityToken: appleToken,
      });

      if (response.data.token) {
        localStorage.setItem("token", response.data.token);

        if (response.data.user) {
          return { token: response.data.token, user: response.data.user };
        }

        const userInfo = await this.getCurrentUser();
        return { token: response.data.token, user: userInfo };
      }

      throw new Error("No se recibió un token de autenticación");
    } catch (error) {
      console.error("Error al iniciar sesión con Apple:", error);
      throw error;
    }
  },

  async signupWithApple(
    appleToken: string,
  ): Promise<{ token: string; user: User }> {
    try {
      // Usar el mismo endpoint /auth/apple - maneja tanto login como signup
      const response = await axiosInstance.post(`/auth/apple`, {
        identityToken: appleToken,
      });

      if (response.data.token) {
        localStorage.setItem("token", response.data.token);

        if (response.data.user) {
          return { token: response.data.token, user: response.data.user };
        }

        const userInfo = await this.getCurrentUser();
        return { token: response.data.token, user: userInfo };
      }

      throw new Error("No se recibió un token de autenticación");
    } catch (error) {
      console.error("Error al registrarse con Apple:", error);
      throw error;
    }
  },

  // Solicitar restablecimiento de contraseña
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    try {
      const response = await axiosInstance.post(
        `/auth/request-password-reset`,
        {
          email,
        },
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error al solicitar restablecimiento de contraseña:",
        error,
      );
      throw error;
    }
  },

  // Restablecer contraseña con token
  async resetPassword(
    token: string,
    newPassword: string,
  ): Promise<{ message: string }> {
    try {
      const response = await axiosInstance.post(`/auth/reset-password`, {
        token,
        newPassword,
      });
      return response.data;
    } catch (error) {
      console.error("Error al restablecer contraseña:", error);
      throw error;
    }
  },

  // Cambiar contraseña de usuario autenticado
  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ message: string }> {
    try {
      const response = await axiosInstance.patch(`/auth/change-password`, {
        currentPassword,
        newPassword,
      });
      return response.data;
    } catch (error) {
      console.error("Error al cambiar contraseña:", error);
      throw error;
    }
  },

  // Actualizar perfil de usuario
  async updateProfile(profileData: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    displayName?: string;
    profileImage?: string;
  }): Promise<{ user: User; message: string }> {
    try {
      const response = await axiosInstance.patch(
        `/profile/update`,
        profileData,
      );

      // Actualizar usuario en el contexto local si existe
      const updatedUser = response.data.user;
      if (updatedUser) {
        // Disparar evento personalizado para que otros componentes se actualicen
        window.dispatchEvent(
          new CustomEvent("userProfileUpdated", {
            detail: { user: updatedUser },
          }),
        );
      }

      return response.data;
    } catch (error) {
      console.error("Error al actualizar perfil:", error);
      throw error;
    }
  },

  // Verificar token de email
  async verifyEmailToken(
    token: string,
  ): Promise<{ success: boolean; message: string; uid?: string }> {
    try {
      // La ruta correcta es /verify-email sin el prefijo profiles
      const response = await axiosInstance.post<{
        success: boolean;
        message: string;
        uid?: string;
      }>(`/verify-email`, { token });

      return response.data;
    } catch (error: unknown) {
      const parsedError = parseApiError(error);
      logger
        .service("AUTH_SERVICE")
        .error(
          "Error en verifyEmailToken",
          error instanceof Error ? error : new Error(String(error)),
          { token: token.substring(0, 8) + "..." },
        );
      throw parsedError;
    }
  },

  /**
   * Obtener el perfil extendido de un especialista
   */
  async getSpecialistExtendedProfile(
    uid: string,
  ): Promise<SpecialistExtendedProfile> {
    try {
      const response = await axiosInstance.get<SpecialistExtendedProfile>(
        `/profile/specialist/${uid}/extended`,
      );

      if (!response.data) {
        throw new Error("Error al obtener perfil extendido");
      }

      return response.data;
    } catch (error: unknown) {
      const parsedError = parseApiError(error);
      logger
        .service("AUTH_SERVICE")
        .error(
          "Error al obtener perfil extendido",
          error instanceof Error ? error : new Error(String(error)),
          { uid },
        );
      throw parsedError;
    }
  },

  /**
   * Actualizar el perfil extendido del especialista autenticado
   */
  async updateSpecialistExtendedProfile(
    extendedData: Partial<ExtendedProfile>,
  ): Promise<SpecialistExtendedProfile> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error("No hay token de autenticación");
      }

      const response = await axiosInstance.patch<SpecialistExtendedProfile>(
        `/profile/specialist/extended`,
        extendedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.data) {
        throw new Error("Error al actualizar perfil extendido");
      }

      return response.data;
    } catch (error: unknown) {
      const parsedError = parseApiError(error);
      logger
        .service("AUTH_SERVICE")
        .error(
          "Error al actualizar perfil extendido",
          error instanceof Error ? error : new Error(String(error)),
        );
      throw parsedError;
    }
  },

  /**
   * Admin: Actualizar el perfil extendido de cualquier especialista
   */
  async adminUpdateSpecialistExtendedProfile(
    targetUid: string,
    extendedData: Partial<ExtendedProfile>,
  ): Promise<SpecialistExtendedProfile> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error("No hay token de autenticación");
      }

      const response = await axiosInstance.put<SpecialistExtendedProfile>(
        `/profile/specialist/${targetUid}/extended`,
        extendedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.data) {
        throw new Error("Error al actualizar perfil extendido");
      }

      return response.data;
    } catch (error: unknown) {
      const parsedError = parseApiError(error);
      logger
        .service("AUTH_SERVICE")
        .error(
          "Error al actualizar perfil extendido (admin)",
          error instanceof Error ? error : new Error(String(error)),
          { targetUid },
        );
      throw parsedError;
    }
  },

  /**
   * Eliminar cuenta de usuario permanentemente
   */
  async deleteAccount(): Promise<{ message: string }> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error("No hay token de autenticación");
      }

      const response = await axiosInstance.delete<{ message: string }>(
        `/profile/delete-account`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Limpiar datos de autenticación después de eliminar la cuenta
      this.logout();

      logger
        .service("AUTH_SERVICE")
        .info("Cuenta de usuario eliminada exitosamente");

      return response.data;
    } catch (error: unknown) {
      const parsedError = parseApiError(error);
      logger
        .service("AUTH_SERVICE")
        .error(
          "Error al eliminar cuenta",
          error instanceof Error ? error : new Error(String(error)),
        );
      throw parsedError;
    }
  },

  // ─── MAGIC LINK ─────────────────────────────────────────────────────────────

  /**
   * Register a cross-device session in the backend after Firebase sends the email link.
   * The sessionId is generated client-side with crypto.randomUUID().
   */
  async createEmailLinkSession(email: string, sessionId: string): Promise<void> {
    await axiosInstance.post(`/auth/magic-link/send`, { email, sessionId });
  },

  /** Poll for cross-device sign-in completion. */
  async pollEmailLinkSession(
    sessionId: string,
  ): Promise<{ status: string; token?: string; user?: User }> {
    const response = await axiosInstance.get(`/auth/magic-link/session/${sessionId}`);
    return response.data;
  },

  /**
   * Exchange a Firebase signInWithEmailLink ID token for our custom JWT.
   * Stores the token and returns the user.
   */
  async completeMagicLink(
    idToken: string,
    sessionId?: string,
  ): Promise<{ token: string; user: User }> {
    const response = await axiosInstance.post(`/auth/magic-link/complete`, {
      idToken,
      sessionId,
    });
    const { token: authToken, user } = response.data;
    if (authToken) {
      localStorage.setItem("token", authToken);
      localStorage.setItem("user", JSON.stringify(user));
    }
    return { token: authToken, user };
  },

  /** @deprecated Use createEmailLinkSession + Firebase sendSignInLinkToEmail instead. */
  async sendMagicLink(email: string, locale?: string): Promise<{ message: string }> {
    const response = await axiosInstance.post(`/auth/magic-link/send`, { email, locale });
    return response.data;
  },

  /** @deprecated Use completeMagicLink instead. */
  async verifyMagicLink(token: string): Promise<{ token: string; user: User }> {
    const response = await axiosInstance.post(`/auth/magic-link/verify`, { token });
    const { token: authToken, user } = response.data;
    if (authToken) {
      localStorage.setItem("token", authToken);
      localStorage.setItem("user", JSON.stringify(user));
    }
    return { token: authToken, user };
  },

  // ─── EMAIL VERIFICATION ──────────────────────────────────────────────────────

  /**
   * Exchange a verified Firebase ID token for our custom JWT.
   * Called after the user clicks the verification link in their email.
   */
  async exchangeVerifiedFirebaseToken(idToken: string): Promise<{ token: string; user: User }> {
    const response = await axiosInstance.post(`/auth/verify-email`, { idToken });
    const { token: authToken, user } = response.data;
    if (authToken) {
      localStorage.setItem("token", authToken);
      localStorage.setItem("user", JSON.stringify(user));
    }
    return { token: authToken, user };
  },

  /** Request a Firebase custom token to resend verification email client-side. */
  async resendVerificationEmail(email: string): Promise<{ firebaseCustomToken: string | null }> {
    const response = await axiosInstance.post(`/auth/resend-verification`, { email });
    return response.data;
  },

  // ─── PASSKEYS ────────────────────────────────────────────────────────────────

  /** Returns true if the browser supports WebAuthn / Passkeys. */
  isPasskeySupported(): boolean {
    return (
      typeof window !== "undefined" &&
      !!window.PublicKeyCredential &&
      typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "function"
    );
  },

  /** Check async whether a platform authenticator is available. */
  async isPlatformAuthenticatorAvailable(): Promise<boolean> {
    if (!this.isPasskeySupported()) return false;
    try {
      return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
      return false;
    }
  },

  /**
   * Register a new passkey for the currently logged-in user.
   * Runs the full WebAuthn attestation ceremony via @simplewebauthn/browser
   * and persists the verified credential on the backend.
   */
  async registerPasskey(_uid: string, _email: string, _displayName: string): Promise<void> {
    // Step 1 – get server-generated attestation options (challenge bound to uid via JWT)
    const optionsRes = await axiosInstance.post("/auth/passkeys/register/options");
    const options = optionsRes.data;

    // Step 2 – run the browser ceremony (OS biometric / PIN prompt)
    let attResp;
    try {
      attResp = await startRegistration(options);
    } catch (err: any) {
      // Rethrow so the caller can distinguish NotAllowedError (user cancelled)
      throw err;
    }

    // Detect a human-readable device name
    const ua = navigator.userAgent;
    const deviceName =
      /iPhone|iPad/i.test(ua) ? "iPhone / iPad" :
      /Android/i.test(ua)     ? "Android" :
      /Mac/i.test(ua)         ? "Mac" :
      /Windows/i.test(ua)     ? "Windows" :
                                "This device";

    // Step 3 – verify on server and persist the credential
    await axiosInstance.post("/auth/passkeys/register/verify", {
      response: attResp,
      deviceName,
    });

    // Flag this browser so the login page can offer the passkey button
    localStorage.setItem("passkey_registered", "1");
  },

  /** List all registered passkeys for the current user. */
  async listPasskeys(): Promise<Array<{
    credentialId: string;
    deviceName: string;
    createdAt: unknown;
    lastUsedAt: unknown;
    disabled?: boolean;
    credentialDeviceType?: string;
    credentialBackedUp?: boolean;
  }>> {
    const response = await axiosInstance.get(`/auth/passkeys`);
    return response.data.passkeys;
  },

  /** Delete a registered passkey. */
  async deletePasskey(credentialId: string): Promise<void> {
    await axiosInstance.delete(`/auth/passkeys/${encodeURIComponent(credentialId)}`);
  },

  // ─── PASSKEY AUTHENTICATION ──────────────────────────────────────────────────

  /** Returns true if this device has a registered passkey saved in localStorage. */
  hasRegisteredPasskey(): boolean {
    return this.isPasskeySupported() && localStorage.getItem("passkey_registered") === "1";
  },

  /**
   * Run the full WebAuthn assertion ceremony and authenticate the user.
   * Returns the same { token, user } shape as a normal login.
   */
  async authenticateWithPasskey(email?: string): Promise<{ token: string; user: User }> {
    // Generate a cryptographically random sessionId (≥32 chars) client-side
    const sessionIdBytes = new Uint8Array(32);
    window.crypto.getRandomValues(sessionIdBytes);
    const sessionId = Array.from(sessionIdBytes, (b) => b.toString(16).padStart(2, "0")).join("");

    // Step 1 – get server-generated assertion options
    const optionsRes = await axiosInstance.post("/auth/passkeys/authenticate/options", {
      sessionId,
      ...(email ? { email } : {}),
    });
    const options = optionsRes.data;

    // Step 2 – run the browser ceremony
    let assertionResp;
    try {
      assertionResp = await startAuthentication(options);
    } catch (err: any) {
      throw err;
    }

    // Step 3 – verify on server and get JWT
    const result = await axiosInstance.post("/auth/passkeys/authenticate/verify", {
      response: assertionResp,
      sessionId,
    });

    const { token, user } = result.data as { token: string; user: User };
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
    return { token, user };
  },
};

export default authService;
